import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import { useTripJson } from '../hooks/data.js';
import { fetchExcursionInterests, setExcursionInterest } from '../lib/excursionsApi.js';

// Excursion id → detail-page slug. Each detail page lives at
// /excursions/{slug} and is responsible for the deep content the
// umbrella card can't carry (operator forecast, lake-conditions panel,
// per-day weather scoring, etc.). The umbrella stays light.
const DETAIL_SLUGS = {
  tue_whale_watch_2026: 'whale-watching',
  thu_mendenhall_canoe: 'mendenhall-canoe',
  endicott_arm_cruise: 'endicott-arm',
};

const INTEREST_OPTIONS = [
  { id: 'in', label: 'In' },
  { id: 'maybe', label: 'Maybe' },
  { id: 'out', label: 'Out' },
];

const SENSITIVITY_TONE = {
  low: 'border-success/40 bg-success/10 text-success',
  moderate: 'border-copper/40 bg-copper/10 text-copper-dark',
  high: 'border-danger/40 bg-danger/10 text-danger',
};

const STATUS_TONE = {
  confirmed: 'border-success/50 bg-success/10 text-success',
  tentative: 'border-copper/50 bg-copper/10 text-copper-dark',
  pending_aggregator: 'border-copper/40 bg-copper/5 text-copper-dark',
  alternate_unbooked: 'border-border-tertiary bg-bg-secondary text-fg-tertiary',
};

const STATUS_LABEL = {
  confirmed: 'confirmed booking',
  tentative: 'tentative · resolving',
  pending_aggregator: 'pending aggregator',
  alternate_unbooked: 'alternate · not booked',
};

export default function Excursions() {
  const { tripId, trip } = useTrip();
  const session = useSession();
  const data = useTripJson(tripId, 'excursions.json');
  const [interests, setInterests] = useState({});
  const [interestsError, setInterestsError] = useState(null);
  const [pending, setPending] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetchExcursionInterests({ tripId });
      setInterests(res.interests || {});
      setInterestsError(null);
    } catch (err) {
      if (err.status === 'api_unavailable') {
        setInterests({});
        setInterestsError(null);
        return;
      }
      setInterestsError(err.message || String(err));
    }
  }, [tripId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const meId = session?.role === 'player' ? session.recipient_id : null;
  const players = trip?.players || [];

  const onPick = useCallback(
    async (excursionId, value) => {
      if (!meId) return;
      const current = interests[excursionId]?.[meId];
      const next = current === value ? null : value;
      setPending(excursionId);
      try {
        const res = await setExcursionInterest({ tripId, excursionId, interest: next });
        setInterests((prev) => ({ ...prev, [excursionId]: res.interest || {} }));
      } catch (err) {
        setInterestsError(err.message || String(err));
      } finally {
        setPending(null);
      }
    },
    [tripId, meId, interests]
  );

  const rawList = data.data?.excursions;
  const groups = useMemo(() => {
    const list = rawList || [];
    const confirmed = [];
    const tentative = [];
    const alternates = [];
    for (const ex of list) {
      const s = ex.booking_status;
      if (s === 'confirmed') confirmed.push(ex);
      else if (s === 'tentative') tentative.push(ex);
      else alternates.push(ex);
    }
    confirmed.sort((a, b) => (a.booking?.date_iso || '').localeCompare(b.booking?.date_iso || ''));
    tentative.sort((a, b) => (a.booking?.date_iso || '').localeCompare(b.booking?.date_iso || ''));
    return { confirmed, tentative, alternates };
  }, [rawList]);

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          itinerary · what we&apos;ve booked
        </p>
        <h1 className="mt-1 font-serif text-2xl">Excursions</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Confirmed bookings up top with prep + format. Tentative and alternate cards below;
          per-player toggles only on cards that need a group decision.
        </p>
      </header>

      {!session.loading && !meId && (
        <div className="card border-copper/40 bg-copper/5 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            view-only
          </p>
          <p className="mt-1 text-sm text-fg-secondary">
            Open the app from your briefing-email link to vote on tentative cards.
          </p>
        </div>
      )}

      {interestsError && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {interestsError}
        </div>
      )}

      {data.loading && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading excursions
        </p>
      )}
      {data.error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          excursions unavailable — {data.error.message}
        </div>
      )}

      {groups.confirmed.length > 0 && (
        <Section heading="Booked">
          <ul className="space-y-3">
            {groups.confirmed.map((ex) => (
              <BookedCard key={ex.id} excursion={ex} />
            ))}
          </ul>
        </Section>
      )}

      {groups.tentative.length > 0 && (
        <Section heading="Resolving — vote your interest">
          <ul className="space-y-3">
            {groups.tentative.map((ex) => (
              <DecisionCard
                key={ex.id}
                excursion={ex}
                players={players}
                interests={interests[ex.id] || {}}
                meId={meId}
                pending={pending === ex.id}
                onPick={onPick}
              />
            ))}
          </ul>
        </Section>
      )}

      {groups.alternates.length > 0 && (
        <Section heading="Alternates — not booked">
          <ul className="space-y-3">
            {groups.alternates.map((ex) => (
              <AlternateCard key={ex.id} excursion={ex} />
            ))}
          </ul>
        </Section>
      )}

      {data.data?.notes && (
        <p className="px-1 text-xs italic text-fg-tertiary">{data.data.notes}</p>
      )}
    </div>
  );
}

function Section({ heading, children }) {
  return (
    <section className="space-y-2">
      <h2 className="font-mono text-[10px] uppercase tracking-wider text-fg-secondary">
        {heading}
      </h2>
      {children}
    </section>
  );
}

function StatusChip({ status }) {
  return (
    <span
      className={[
        'rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider',
        STATUS_TONE[status] || STATUS_TONE.alternate_unbooked,
      ].join(' ')}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function BookedCard({ excursion }) {
  const [openPrep, setOpenPrep] = useState(false);
  const b = excursion.booking || {};
  const dateLabel = b.date_iso
    ? new Date(`${b.date_iso}T12:00:00Z`).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: b.tz || 'America/Juneau',
      })
    : '';

  return (
    <li className="card overflow-hidden border-success/40">
      <header className="space-y-2 border-b border-success/30 bg-success/5 px-4 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-serif text-lg leading-tight">{excursion.name}</h3>
          <StatusChip status={excursion.booking_status} />
        </div>
        <p className="font-mono text-[11px] uppercase tracking-wider text-fg-secondary">
          {dateLabel} · {b.start_local}–{b.end_local} · {excursion.duration_hours}h
        </p>
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          ${b.per_person}/person · group of 3 = ${b.group_total?.toLocaleString?.() || excursion.group_total_for_3.toLocaleString()}
          {b.aggregator ? ` · via ${b.aggregator}` : ''}
        </p>
      </header>

      <div className="space-y-3 px-4 py-3">
        {excursion.format_note && (
          <p className="rounded-md border border-copper/40 bg-copper/10 p-2 text-xs text-copper-dark">
            <span className="font-mono text-[10px] uppercase tracking-wider">format · </span>
            {excursion.format_note}
          </p>
        )}
        {b.context_note && (
          <p className="rounded-md border border-copper/30 bg-copper/5 p-2 text-xs text-fg-secondary">
            <span className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
              heads-up ·{' '}
            </span>
            {b.context_note}
          </p>
        )}
        {excursion.what_to_expect && (
          <p className="text-sm text-fg-primary">
            <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              what to expect ·{' '}
            </span>
            {excursion.what_to_expect}
          </p>
        )}
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          <span
            className={[
              'rounded-full border px-2 py-0.5',
              SENSITIVITY_TONE[excursion.weather_sensitivity] || '',
            ].join(' ')}
          >
            weather {excursion.weather_sensitivity}
          </span>
          {excursion.weather_note && (
            <span className="normal-case tracking-normal text-fg-secondary">
              {excursion.weather_note}
            </span>
          )}
        </div>

        {excursion.highlights?.length > 0 && (
          <ul className="space-y-1 text-sm text-fg-secondary">
            {excursion.highlights.map((h) => (
              <li key={h} className="flex gap-2">
                <span className="text-fg-tertiary" aria-hidden>
                  ◦
                </span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        <div>
          <button
            type="button"
            onClick={() => setOpenPrep((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline"
          >
            {openPrep ? 'hide what to bring + prep' : 'what to bring + prep'}
          </button>
          {openPrep && (
            <div className="mt-2 space-y-2 rounded-md bg-bg-secondary p-3 text-sm text-fg-secondary">
              {excursion.what_to_bring?.length > 0 && (
                <ul className="space-y-1">
                  {excursion.what_to_bring.map((p) => (
                    <li key={`b-${p}`} className="flex gap-2">
                      <span className="text-fg-tertiary" aria-hidden>
                        □
                      </span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
              {excursion.prep_checklist?.length > 0 && (
                <ul className="space-y-1 border-t border-border-tertiary pt-2">
                  {excursion.prep_checklist.map((p) => (
                    <li key={`p-${p}`} className="flex gap-2">
                      <span className="text-fg-tertiary" aria-hidden>
                        →
                      </span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <p className="rounded-md border border-dashed border-border-tertiary bg-bg-secondary px-3 py-2 text-center font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          operator name + booking link land in the next refinement patch
        </p>

        <DetailLink excursionId={excursion.id} />
      </div>
    </li>
  );
}

function DecisionCard({ excursion, players, interests, meId, pending, onPick }) {
  const [openPrep, setOpenPrep] = useState(false);
  const consensus = summarize(interests, players);
  const b = excursion.booking || {};
  const dateLabel = b.date_iso
    ? new Date(`${b.date_iso}T12:00:00Z`).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: b.tz || 'America/Juneau',
      })
    : '';

  return (
    <li className="card overflow-hidden border-copper/40">
      <header className="space-y-2 border-b border-copper/30 bg-copper/5 px-4 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-serif text-lg leading-tight">{excursion.name}</h3>
          <StatusChip status={excursion.booking_status} />
        </div>
        {excursion.tentative_subline && (
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-copper-dark">
            {excursion.tentative_subline}
          </p>
        )}
        {b.date_iso && (
          <p className="font-mono text-[11px] uppercase tracking-wider text-fg-secondary">
            {dateLabel} · {b.start_local}–{b.end_local} · {excursion.duration_hours}h
          </p>
        )}
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          ${excursion.base_price_per_person}/person · group ≈ ${excursion.group_total_for_3.toLocaleString()}
        </p>
      </header>
      <div className="space-y-3 px-4 py-3">
        {excursion.wilman_blurb && (
          <p className="rounded-md border border-copper/40 bg-copper/10 p-3 font-serif text-sm italic leading-relaxed text-copper-dark">
            {excursion.wilman_blurb}
          </p>
        )}
        {excursion.tentative_outcomes?.length > 0 && (
          <div className="space-y-1 rounded-md border border-border-tertiary bg-bg-secondary p-2 text-xs text-fg-secondary">
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              three possible outcomes
            </p>
            <ol className="space-y-0.5 pl-4 list-decimal">
              {excursion.tentative_outcomes.map((o) => (
                <li key={o}>{o}</li>
              ))}
            </ol>
          </div>
        )}
        {excursion.what_to_expect && (
          <p className="text-sm text-fg-primary">{excursion.what_to_expect}</p>
        )}
        {b.context_note && (
          <p className="rounded-md border border-copper/30 bg-copper/5 p-2 text-xs text-fg-secondary">
            <span className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
              status ·{' '}
            </span>
            {b.context_note}
          </p>
        )}
        {excursion.weather_note && (
          <p className="rounded-md bg-bg-secondary p-2 text-xs text-fg-secondary">
            <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              weather note ·{' '}
            </span>
            {excursion.weather_note}
          </p>
        )}
        {excursion.highlights?.length > 0 && (
          <ul className="space-y-1 text-sm text-fg-secondary">
            {excursion.highlights.map((h) => (
              <li key={h} className="flex gap-2">
                <span className="text-fg-tertiary" aria-hidden>
                  ◦
                </span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        <div>
          <button
            type="button"
            onClick={() => setOpenPrep((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline"
          >
            {openPrep ? 'hide prep' : 'prep checklist'}
          </button>
          {openPrep && (
            <ul className="mt-2 space-y-1 rounded-md bg-bg-secondary p-3 text-sm text-fg-secondary">
              {(excursion.prep_checklist || []).map((p) => (
                <li key={p} className="flex gap-2">
                  <span className="text-fg-tertiary" aria-hidden>
                    □
                  </span>
                  <span>{p}</span>
                </li>
              ))}
              {excursion.tracy_arm_note && (
                <li className="mt-1 border-t border-border-tertiary pt-2 text-xs italic text-fg-tertiary">
                  {excursion.tracy_arm_note}
                </li>
              )}
            </ul>
          )}
        </div>

        <section
          aria-labelledby={`votes-${excursion.id}`}
          className="rounded-md border border-border-tertiary bg-bg-primary p-3"
        >
          <header className="flex items-baseline justify-between">
            <h4
              id={`votes-${excursion.id}`}
              className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
            >
              group consensus
            </h4>
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-secondary">
              {consensus.summary}
            </p>
          </header>
          <ul className="mt-2 space-y-1.5">
            {players.map((p) => {
              const value = interests[p.id] || null;
              const isMe = meId === p.id;
              return (
                <li key={p.id} className="flex items-center justify-between gap-2">
                  <span
                    className={[
                      'font-mono text-[11px] uppercase tracking-wider',
                      isMe ? 'text-action' : 'text-fg-secondary',
                    ].join(' ')}
                  >
                    {p.display_name || p.name}
                    {isMe && <span className="ml-1 text-fg-tertiary">(you)</span>}
                  </span>
                  {isMe ? (
                    <div className="flex gap-1" role="radiogroup" aria-label={`${p.name} interest`}>
                      {INTEREST_OPTIONS.map((opt) => {
                        const selected = value === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            disabled={pending}
                            onClick={() => onPick(excursion.id, opt.id)}
                            className={[
                              'min-w-[3rem] rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition disabled:opacity-50',
                              selected
                                ? 'border-action bg-action text-action-fg'
                                : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
                            ].join(' ')}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <ReadOnlyVote value={value} />
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <DetailLink excursionId={excursion.id} />
      </div>
    </li>
  );
}

function AlternateCard({ excursion }) {
  return (
    <li className="card overflow-hidden">
      <header className="space-y-1 border-b border-border-tertiary bg-bg-secondary px-4 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-serif text-base leading-tight">{excursion.name}</h3>
          <StatusChip status={excursion.booking_status} />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          ${excursion.base_price_per_person}/person · {excursion.duration_hours}h · group ≈ ${excursion.group_total_for_3.toLocaleString()}
        </p>
      </header>
      <div className="space-y-3 px-4 py-3 text-sm text-fg-secondary">
        <p>{excursion.summary}</p>
        <DetailLink excursionId={excursion.id} />
      </div>
    </li>
  );
}

function DetailLink({ excursionId }) {
  const slug = DETAIL_SLUGS[excursionId];
  if (!slug) return null;
  return (
    <Link
      to={`/excursions/${slug}`}
      className="block rounded-md border border-action/40 bg-action/5 px-3 py-2 text-center font-mono text-[10px] uppercase tracking-wider text-action hover:bg-action/10"
    >
      view detail + forecast ›
    </Link>
  );
}

function ReadOnlyVote({ value }) {
  const map = {
    in: { label: 'In', cls: 'border-success/40 bg-success/10 text-success' },
    maybe: { label: 'Maybe', cls: 'border-copper/40 bg-copper/10 text-copper-dark' },
    out: { label: 'Out', cls: 'border-danger/40 bg-danger/10 text-danger' },
  };
  if (!value || !map[value]) {
    return (
      <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        — not yet
      </span>
    );
  }
  const { label, cls } = map[value];
  return (
    <span
      className={['rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider', cls].join(' ')}
    >
      {label}
    </span>
  );
}

function summarize(interests, players) {
  let inCount = 0;
  let maybeCount = 0;
  let outCount = 0;
  let votedCount = 0;
  for (const p of players) {
    const v = interests[p.id];
    if (v === 'in') inCount += 1;
    else if (v === 'maybe') maybeCount += 1;
    else if (v === 'out') outCount += 1;
    if (v) votedCount += 1;
  }
  const total = players.length;
  if (votedCount === 0) return { summary: 'no votes yet', inCount, maybeCount, outCount };
  const parts = [];
  if (inCount) parts.push(`${inCount} in`);
  if (maybeCount) parts.push(`${maybeCount} maybe`);
  if (outCount) parts.push(`${outCount} out`);
  if (votedCount < total) parts.push(`${total - votedCount} pending`);
  return { summary: parts.join(' · '), inCount, maybeCount, outCount };
}
